import React, { useEffect, useState } from "react";
import { Card, Button, Form, Collapse, ListGroup, Placeholder, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { BsPencilSquare, BsThreeDotsVertical, BsTrash3 } from "react-icons/bs";
import { RiAddLine, RiCompassDiscoverLine, RiFileList3Line } from "react-icons/ri";
import { HiOutlineSave } from "react-icons/hi";
import { GrDocumentPdf } from "react-icons/gr";
import SearchModel from "../Models/SearchModel";
import UploadModel from "../Models/UploadModel";
import { fetchPdfs } from "../Services/api";
import { embedPdf, deletePdf } from "../Services/api";

const Sidebar = ({
    showUpload,
    setShowUpload,
    setToggle1,
    toggle1,
    collapsed,
    setCollapsed,
    selectedPdfId,
    setSelectedPdfId,
    onEmbeddedSuccess,
}) => {
    const [pdfs, setPdfs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [openRow, setOpenRow] = useState(null);
    const [embedding, setEmbedding] = useState(false);
    const [embedMsg, setEmbedMsg] = useState("");
    const [embedStatus, setEmbedStatus] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [isFiltered, setIsFiltered] = useState(false);
    const [filteredPdfs, setFilteredPdfs] = useState([]);

    const refreshPdfs = async (signal) => {
        setLoading(true);
        try {
            const list = await fetchPdfs(signal);
            setPdfs(Array.isArray(list) ? list : []);
            setErr("");
        } catch (e) {
            if (!signal || e.name !== "AbortError") setErr(e.message || "Failed to load PDFs");
        } finally {
            setLoading(false);
        }
    };

    const listToRender = isFiltered ? filteredPdfs : pdfs;


    useEffect(() => {
        const ctrl = new AbortController();
        refreshPdfs(ctrl.signal);
        return () => ctrl.abort();
    }, []);

    async function handleEmbed() {
        if (!selectedPdfId) {
            setEmbedMsg("Please select a PDF first.");
            return;
        }
        setEmbedMsg("");
        setEmbedding(true);
        try {
            await embedPdf(selectedPdfId);
            setEmbedMsg("Embedded Successfully.");
            setEmbedStatus("success");
            const current = pdfs.find(p => p.id === selectedPdfId);
            onEmbeddedSuccess?.({ id: selectedPdfId, name: current?.filename });
        } catch (e) {
            const extra = e?.message ? ` ${e.message}` : "";
            setEmbedMsg(`Embedded Failed.${extra}`);
            setEmbedStatus("error");
        } finally {
            setEmbedding(false);
        }
    }

    async function handleRemove(item) {
        setErr("");
        setRemovingId(item.id);
        setPdfs((prev) => prev.filter((p) => p.id !== item.id));
        if (selectedPdfId === item.id) setSelectedPdfId(null);
        setOpenRow(null);
        try {
            await deletePdf(item.id);
            await refreshPdfs();
        } catch (e) {
            setErr(e.message || "Failed to delete");
            await refreshPdfs();
        } finally {
            setRemovingId(null);
        }
    }


    return (
        <>
            <div className="p-3" style={{ height: "calc(100dvh - 67px)" }}>
                <Card className={`sources-card d-flex flex-column h-100 ${collapsed ? "collapsed" : ""}`}>
                    <div className="hstack justify-content-between border-bottom mb-4" style={{ padding: 10, paddingLeft: 38 }}>
                        {!collapsed && <h6 className="mb-0 fs_18 fw-bold">Sources</h6>}
                        <button className="btn btn-light" onClick={() => setCollapsed(!collapsed)}>
                            {collapsed ? <GoSidebarExpand size={24} /> : <GoSidebarCollapse size={24} />}
                        </button>
                    </div>
                    <div className="sources-actions justify-content-center">
                        <button className="btn-chip" variant="dark" onClick={() => setShowUpload(true)} title="Add">
                            <RiAddLine size={18} />
                            {!collapsed && <span className="ms-1">Add</span>}
                        </button>
                        {!collapsed && (
                            <button className="btn-chip" variant="dark" onClick={setToggle1} title="Discover">
                                <RiCompassDiscoverLine size={18} />
                                <span className="ms-1">Discover</span>
                            </button>
                        )}
                    </div>

                    {collapsed && (
                        <div className="mt-3 d-flex flex-column align-items-center gap-3">
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                listToRender.map((it) => (
                                    <OverlayTrigger
                                        key={it.id}
                                        placement="right"
                                        overlay={<Tooltip id={`tip-${it.id}`}>{it.filename}</Tooltip>}
                                    >
                                        <div
                                            className={`d-flex align-items-center justify-content-center  icon-swap`}
                                            style={{
                                                boxShadow: selectedPdfId === it.id ? "0 0 0 2px #111" : "none",
                                            }}
                                            onClick={() => setSelectedPdfId(it.id)}
                                            aria-label={it.filename}
                                        >
                                            <GrDocumentPdf className="text-danger fs_25" />
                                        </div>
                                    </OverlayTrigger>
                                ))
                            )}
                        </div>
                    )}

                    {collapsed && (
                        <div className="p-3 mt-auto text-center">
                            <button className="btn-chip" onClick={handleEmbed} title="Save Source in Vector DB">
                                {embedding ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" /> Embedding...
                                    </>
                                ) : (
                                    <HiOutlineSave className="fs_20" />
                                )}
                            </button>
                            {embedMsg && (
                                <div
                                    className={`fw-bold mt-2 ${embedStatus === "success" ? "text-success" : embedStatus === "error" ? "text-danger" : ""
                                        }`}
                                >
                                    {embedMsg}
                                </div>
                            )}
                        </div>
                    )}

                    {!collapsed && (
                        <>
                            <div className="flex-grow-1 overflow-auto">
                                <div className="divider" />
                                {loading ? (
                                    <div className="pt-2" aria-busy="true">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="pdf-row p-3 d-flex align-items-center gap-3">
                                                <Placeholder as="div" animation="glow" className="d-flex align-items-center gap-3 w-100">
                                                    <Placeholder className="rounded" style={{ width: 36, height: 36 }} />
                                                    <Placeholder className="rounded-2" style={{ height: 14, width: "60%" }} />
                                                    <Placeholder className="ms-auto rounded" style={{ width: 20, height: 20 }} />
                                                </Placeholder>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        {!err && !loading && listToRender.length === 0 && (
                                            <div className="empty-state d-flex flex-column align-items-center justify-content-center">
                                                <RiFileList3Line className="empty-icon" />
                                                <div className="empty-title">
                                                    {isFiltered ? 'No results found' : 'Saved sources will appear here'}
                                                </div>
                                                {!isFiltered && (
                                                    <div className="empty-desc">
                                                        Click <strong>Add</strong> above to add PDFs
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {err && <div className="text-danger px-3 py-2">{err}</div>}

                                        {listToRender.length > 0 && (
                                            <div className="pt-2">
                                                {listToRender.map((it) => (
                                                    <div
                                                        key={it.id}
                                                        className={`pdf-row p-3 d-flex align-items-center gap-3 position-relative ${openRow === it.id ? "is-active" : ""
                                                            }`}
                                                    >
                                                        <div className="icon-swap position-relative d-flex align-items-center justify-content-center">
                                                            <GrDocumentPdf className="pdf-icon fs-3 text-danger" />
                                                            <button
                                                                className="btn btn-threeDot menu-icon d-flex align-items-center justify-content-center p-2"
                                                                onClick={() => setOpenRow((r) => (r === it.id ? null : it.id))}
                                                                aria-expanded={openRow === it.id}
                                                                aria-controls={`row-actions-${it.id}`}
                                                                title="More"
                                                                type="button"
                                                            >
                                                                <BsThreeDotsVertical className="fw-bold fs_25" />
                                                            </button>
                                                        </div>

                                                        <span className="file-name fs_18 fw-bold text-truncate">{it.filename}</span>

                                                        <Form.Check
                                                            id={`pdf-${it.id}`}
                                                            value={it.id}
                                                            checked={selectedPdfId === it.id}
                                                            onChange={() => setSelectedPdfId(it.id)}
                                                            className="ms-auto fs_25"
                                                        />

                                                        <div
                                                            className="position-absolute start-0 mt-2"
                                                            style={{ top: "100%", zIndex: 1050, minWidth: 220 }}
                                                        >
                                                            <Collapse in={openRow === it.id} dimension="height">
                                                                <div id={`row-actions-${it.id}`}>
                                                                    <ListGroup>
                                                                        <ListGroup.Item
                                                                            action
                                                                            onClick={() => handleRemove(it)}
                                                                            disabled={removingId === it.id}
                                                                        >
                                                                            <BsTrash3 className="text-danger fw-bold" />{" "}
                                                                            {removingId === it.id ? "Removing…" : "Remove source"}
                                                                        </ListGroup.Item>
                                                                        <ListGroup.Item action>
                                                                            <BsPencilSquare className="text-dark fw-bold" /> Rename source
                                                                        </ListGroup.Item>
                                                                    </ListGroup>
                                                                </div>
                                                            </Collapse>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="p-3 text-center mt-auto">
                                <button variant="dark" className="btn-chip p-2" onClick={handleEmbed} disabled={embedding}>
                                    {embedding ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" /> Embedding...
                                        </>
                                    ) : (
                                        "Save Source in Vector DB"
                                    )}
                                </button>

                                {embedMsg && (
                                    <div
                                        className={`fw-bold mt-2 ${embedStatus === "success" ? "text-success" : embedStatus === "error" ? "text-danger" : ""
                                            }`}
                                    >
                                        {embedMsg}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Card>
            </div>

            <UploadModel
                Show={showUpload}
                Hide={() => setShowUpload(false)}
                size="lg"
                onUploaded={(res) => {
                    setShowUpload(false);
                    refreshPdfs();
                    setSelectedPdfId?.(res.id);
                }}
            />
            <SearchModel Show={!toggle1}
                Hide={setToggle1}
                size={"lg"}
                onResults={(results) => {
                    setIsFiltered(true);
                    setFilteredPdfs(results || []);
                    if ((results || []).length > 0) {
                        setSelectedPdfId(results[0].id);
                    } else {
                        setSelectedPdfId(null);
                    }
                }}
                onClose={async () => {
                    setIsFiltered(false);
                    setFilteredPdfs([]);
                    await refreshPdfs();
                }}
            />
        </>
    );
};

export default Sidebar;
